/**
 * Zone Drawing Store
 *
 * Manages transient state for Zone polygon drawing in the map editor.
 * Exclusive with `useObjectTypeStore.selectedType` — mutual cancel is enforced
 * at integration points (ZoneSection, useObjectCreation guard).
 *
 * Lifecycle:
 *   idle → start(ctx) → drawing(points=[])
 *        → addPoint/setHover/removeLastPoint during drawing
 *        → complete() returns final points + resets to idle
 *        → cancel() resets to idle without returning data
 *
 * Minimum points to complete: 3 (enforced via `canComplete()`).
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Point } from '../api/types/layerMapping';

export type ZoneDrawingMode = 'idle' | 'drawing';

export interface ZoneDrawingContext {
  objectTypeId: number;
  projectId: number;
  floorId: string;
  /**
   * 지정하면 새 zone 을 만들지 않고 이 cell 의 폴리곤만 갈아끼운다
   * (자동 생성된 원 모양을 보고 직접 다시 그리는 용도).
   * 이름·색·memberIds 등 기존 속성은 그대로 승계된다.
   */
  replaceCellId?: string;
}

export interface ZoneDrawingState {
  mode: ZoneDrawingMode;
  points: Point[];
  hoverPoint: Point | null;
  objectTypeId: number | null;
  projectId: number | null;
  floorId: string | null;
  /** null 이면 신규 생성, 값이 있으면 그 cell 의 폴리곤 교체. */
  replaceCellId: string | null;

  start: (ctx: ZoneDrawingContext) => void;
  addPoint: (p: Point) => void;
  setHover: (p: Point | null) => void;
  removeLastPoint: () => void;
  cancel: () => void;
  complete: () => Point[];
  canComplete: () => boolean;
}

export const ZONE_DRAWING_MIN_POINTS = 3;

const INITIAL_STATE: Pick<
  ZoneDrawingState,
  | 'mode'
  | 'points'
  | 'hoverPoint'
  | 'objectTypeId'
  | 'projectId'
  | 'floorId'
  | 'replaceCellId'
> = {
  mode: 'idle',
  points: [],
  hoverPoint: null,
  objectTypeId: null,
  projectId: null,
  floorId: null,
  replaceCellId: null,
};

export const useZoneDrawingStore = create<ZoneDrawingState>()(
  devtools(
    (set, get) => ({
      ...INITIAL_STATE,

      start: (ctx) =>
        set(
          {
            mode: 'drawing',
            points: [],
            hoverPoint: null,
            objectTypeId: ctx.objectTypeId,
            projectId: ctx.projectId,
            floorId: ctx.floorId,
            replaceCellId: ctx.replaceCellId ?? null,
          },
          false,
          'zoneDrawing/start'
        ),

      addPoint: (p) =>
        set(
          (s) => ({ points: [...s.points, p] }),
          false,
          'zoneDrawing/addPoint'
        ),

      setHover: (p) =>
        set({ hoverPoint: p }, false, 'zoneDrawing/setHover'),

      removeLastPoint: () =>
        set(
          (s) => ({ points: s.points.slice(0, -1) }),
          false,
          'zoneDrawing/removeLastPoint'
        ),

      cancel: () => set({ ...INITIAL_STATE }, false, 'zoneDrawing/cancel'),

      complete: () => {
        const finalPoints = get().points.slice();
        set({ ...INITIAL_STATE }, false, 'zoneDrawing/complete');
        return finalPoints;
      },

      canComplete: () => get().points.length >= ZONE_DRAWING_MIN_POINTS,
    }),
    { name: 'zoneDrawingStore' }
  )
);
